import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import Loading from "../../components/Loading";
import { callRemote, GET } from "../../service/RemoteService";
import { determineIfTokenError } from "../../store/AuthThunks";
import { formatPhoneNumber } from "../../utils/utils";
import DispatchPhase from "./dispatch/DispatchPhase";
import DispatchCheck from "./dispatch/DispatchCheck";
import { useChecklist } from "./dispatch/useChecklist";
import {
  SUPPORT_CONTACT,
  SHOOT_GUIDE_URL,
  TUTORIAL_VIDEO_URL,
  cancelShootFormUrl,
  customerPhoneOf,
  initialsOf,
  introMessage,
  smsHref,
  telHref,
} from "./dispatch/dispatchLinks";
import confirmShootImg from "../../assets/dispatch/confirm-shoot-date.jpg";
import uploadPhotosImg from "../../assets/dispatch/upload-photos.jpg";
import submitShootImg from "../../assets/dispatch/submit-shoot.jpg";
import "../../views/photographers/ShootDispatch.css";

const PRE_SHOOT_KEYS = ["pre-call", "pre-time", "pre-wash"];
const DURING_SHOOT_KEYS = ["day-confirm"];
const POST_SHOOT_KEYS = ["post-done"];

export const ShootDispatch = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [shoot, setShoot] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [openPhase, setOpenPhase] = useState(1);

  // There is no single-shoot endpoint, so this reuses the photographer's own
  // shoot list and picks the one being viewed. That keeps the page scoped to
  // shoots this photographer is assigned to, and it survives a refresh or a
  // link pasted into a phone, which passing the shoot through router state
  // would not.
  const getShoot = useCallback(async () => {
    try {
      const shoots = await callRemote(getAccessTokenSilently, GET, "/photographers/shoots");
      const match = (shoots || []).find((s) => String(s.id) === String(id));
      setShoot(match || null);
    } catch (e) {
      dispatch(determineIfTokenError(e));
    } finally {
      setLoaded(true);
    }
  }, [dispatch, getAccessTokenSilently, id]);

  useEffect(() => {
    getShoot();
  }, [getShoot]);

  const { isChecked, toggle, countChecked } = useChecklist(shoot && shoot.vin);

  const togglePhase = (phase) => setOpenPhase((current) => (current === phase ? null : phase));

  if (!loaded) {
    return <Loading />;
  }

  if (!shoot) {
    return (
      <div className="shoot-dispatch">
        <div className="masthead">
          <div className="brand">Exotic Car Trader · Shoot Dispatch</div>
          <h1>Shoot not found</h1>
          <p className="meta">
            This shoot is not assigned to you, or it is no longer active.
          </p>
        </div>
        <div className="phase-body">
          <Link className="btn primary" to="/my-shoots">Back to My Shoots</Link>
        </div>
      </div>
    );
  }

  const customerPhone = customerPhoneOf(shoot);
  const callHref = telHref(customerPhone);
  const textHref = smsHref(customerPhone, introMessage(shoot.car));
  const location = [shoot.city, shoot.state].filter(Boolean).join(", ");

  const renderCheck = (itemKey, label, hint) => (
    <DispatchCheck
      itemKey={itemKey}
      label={label}
      hint={hint}
      checked={isChecked(itemKey)}
      onToggle={toggle}
    />
  );

  return (
    <div className="shoot-dispatch">
      <header className="bar">
        <span className="t">{shoot.car}</span>
        <span className="vin mono">{shoot.vin}</span>
      </header>

      <Link className="back-link" to="/my-shoots">← My Shoots</Link>

      <div className="masthead">
        <div className="brand">Exotic Car Trader · Shoot Dispatch</div>
        <h1>{shoot.car}</h1>
        <p className="meta">
          {location}
          {location && <span> &nbsp;·&nbsp; </span>}
          <span className="mono">VIN {shoot.vin}</span>
        </p>
        <p className="meta">
          Questions at any point? Call or text{" "}
          <strong>
            {SUPPORT_CONTACT.name} —{" "}
            <a href={telHref(SUPPORT_CONTACT.phone)}>{formatPhoneNumber(SUPPORT_CONTACT.phone)}</a>
          </strong>
        </p>
      </div>

      <DispatchPhase
        number={1}
        name="Pre-shoot"
        subtitle="Do this now — call, prep, confirm"
        isOpen={openPhase === 1}
        onToggle={togglePhase}
        done={countChecked(PRE_SHOOT_KEYS)}
        total={PRE_SHOOT_KEYS.length}
      >
        <p className="step-note">
          Call the customer and introduce yourself as the photographer shooting their car for
          Exotic Car Trader.
        </p>

        <div className="customer-card">
          <div className="row">
            <div className="avatar">{initialsOf(shoot.customerName)}</div>
            <div className="who">
              <div className="n">{shoot.customerName}</div>
              <div className="p">
                {customerPhone ? formatPhoneNumber(customerPhone) : "No phone number on file"}
              </div>
            </div>
          </div>
          <div className="btn-row">
            <a className={callHref ? "btn" : "btn disabled"} href={callHref || undefined}>
              Call customer
            </a>
            <a className={textHref ? "btn" : "btn disabled"} href={textHref || undefined}>
              Text customer
            </a>
          </div>
        </div>

        <p className="step-note" style={{ marginBottom: "6px" }}>
          No answer? Tap <strong>Text customer</strong> above — it opens Messages with the
          customer's number and this message already filled in. Just add your name and send:
        </p>
        <div className="script">{introMessage(shoot.car)}</div>

        {renderCheck("pre-call", "Called customer and introduced yourself", "Text if no answer")}
        {renderCheck("pre-time", "Asked when / where they’d like to shoot", "Let them know it only takes about 30 minutes")}
        {renderCheck("pre-wash", "Asked them to have the car washed and interior cleared", "No paper or clutter in the car")}

        <h3>Confirm the date and shoot here</h3>
        <div className="callout signal">
          ONLY submit this once you have determined a time and place with the customer. Need to
          reschedule? Confirm here again.
        </div>
        <Link className="btn orange" to="/my-shoots">Confirm shoot on My Shoots →</Link>
        <div className="ref-img">
          <img
            src={confirmShootImg}
            alt="My Shoots: tap the blue calendar icon under Actions to confirm the shoot date"
            loading="lazy"
          />
        </div>
      </DispatchPhase>

      <DispatchPhase
        number={2}
        name="During shoot"
        subtitle="At the car on shoot day"
        isOpen={openPhase === 2}
        onToggle={togglePhase}
        done={countChecked(DURING_SHOOT_KEYS)}
        total={DURING_SHOOT_KEYS.length}
      >
        <div className="hard-rules">
          <div className="hr-head">Shoot rules</div>
          <ul>
            <li>
              <span className="no">✕</span>
              <span><strong>NEVER shoot in the GRASS.</strong> Pavement only — and no school or store parking lots.</span>
            </li>
            <li><span className="no">✕</span><span>DO NOT shoot sales photos in PORTRAIT mode</span></li>
            <li><span className="no">✓</span><span>DO shoot sales photos in LANDSCAPE mode</span></li>
            <li><span className="no">✓</span><span>Shoot in .JPEG format</span></li>
            <li><span className="no">✓</span><span>Have the customer pull the car into FULL SUNLIGHT — not shade</span></li>
            <li><span className="no">✓</span><span>Bring a rag in case anything needs to be wiped down</span></li>
          </ul>
        </div>
        <h3>Shoot Guide — this tells you every photo to get</h3>
        <div className="callout signal">
          Work through this guide WHILE you shoot. It walks you through every required shot in order
          — exterior, details, engine bay, undercarriage, interior, video. If you follow the form,
          you will not miss a photo.
        </div>

        <div className="callout signal">
          GET SEVERAL SHOTS OF THE UNDERCARRIAGE OF THE CAR. We need to see what the bottom looks
          like.
        </div>

        <p className="step-note">
          The full shoot guide has every required angle, reference photos, video and reel
          requirements. Go through it before the shoot for practice. Open it before shoot day and add it to your home screen — it works with no
          service.
        </p>
        <a className="btn primary" href={SHOOT_GUIDE_URL} target="_blank" rel="noopener noreferrer">
          Open the shoot guide →
        </a>
        <a className="btn subtle" href={TUTORIAL_VIDEO_URL} target="_blank" rel="noopener noreferrer">
          Watch the quick tutorial video
        </a>

        {renderCheck("day-confirm", "Called / confirmed with the customer before leaving", "Quick confirmation that the shoot is complete")}
      </DispatchPhase>

      <DispatchPhase
        number={3}
        name="Post-shoot"
        subtitle="Upload everything & get paid"
        isOpen={openPhase === 3}
        onToggle={togglePhase}
        done={countChecked(POST_SHOOT_KEYS)}
        total={POST_SHOOT_KEYS.length}
      >
        <div className="steps-123">
          <div className="s123">
            <span><em>Name the 7–10 creative shots your “IG Handle”.</em></span>
          </div>
          <div className="s123">
            <span>
              <em>Upload ALL photos and video</em> at the link below. Format must be .JPG, NO
              watermarks.
              <small>
                <strong>HOW:</strong> Click the WHERE link below, click “Choose From Computer”,
                select all photos and video, press “Upload”. Note: you are not uploading a folder —
                upload the individual photos and video.
                <br />
                <strong>TAKING TOO LONG?</strong> If upload time is too long due to file size, run
                the photos through Lightroom and resize to ~500KB per picture.
              </small>
              <div className="ref-img" style={{ margin: "10px 0 2px" }}>
                <img src={uploadPhotosImg} alt="My Shoots: click Upload Photos under Links" loading="lazy" />
              </div>
            </span>
          </div>
          <div className="s123">
            <span>
              <em>Once everything is completely uploaded, SUBMIT the completion form.</em>
              <small>Mandatory to be paid.</small>
              <div className="ref-img" style={{ margin: "10px 0 2px" }}>
                <img src={submitShootImg} alt="My Shoots: click the green check under Actions to submit" loading="lazy" />
              </div>
            </span>
          </div>
        </div>

        <div className="vehicle-line">
          {shoot.car} <span className="mono">/ VIN: {shoot.vin}</span>
        </div>

        <h3>WHERE:</h3>
        <Link className="btn orange" to="/my-shoots">Upload &amp; submit on My Shoots →</Link>

        {renderCheck("post-done", "Everything uploaded and completion form submitted", "You're done — payment processes from here")}
      </DispatchPhase>

      <div className="contact">
        If you have any questions, call/text{" "}
        <strong>
          {SUPPORT_CONTACT.name} —{" "}
          <a href={telHref(SUPPORT_CONTACT.phone)}>{formatPhoneNumber(SUPPORT_CONTACT.phone)}</a>
        </strong>
      </div>
      <a
        className="cancel-link"
        href={cancelShootFormUrl(shoot.vin)}
        target="_blank"
        rel="noopener noreferrer"
      >
        Need to cancel? Submit this form and we will book a new photographer
      </a>
    </div>
  );
};

export default withAuthenticationRequired(ShootDispatch, {
  onRedirecting: () => <Loading />,
});
